/* eslint-disable import/no-cycle */
import React from 'react';
import {
  ElementDescription,
  UISchemaElement,
  UISchemaElementTranslations,
  UISchemaScope,
} from './types';

/**
  This function converts a nested UISchema into a flat array of ElementDescription objects.

  e.g.:
  {
    "myField": {
      "component": "TextField",
      "scopes": ["project"],
      "translation": {
        "title": {
          "de": "Logo",
          "en": "Logo"
        },
      }
    }
  }
  becomes:
  [
    {
      key: "myField",
      component: "TextField",
      scopes: ["project"],
      translation: {
        title: {
          de: "Logo",
          en: "Logo"
        }
      }
    }
  ]
*/
export function flattenUISchema(
  schema: Record<string, UISchemaElement>,
): ElementDescription[] {
  if (!schema) return [];
  return Object.entries(schema as Record<string, Record<string, any>>).map(
    ([key, data]) => ({ key, ...data }),
  ) as ElementDescription[];
}

interface ValidateTranslationProps {
  translation: UISchemaElementTranslations;
  key: ElementDescription['key'];
}

/**
  This function ensures that the most relevant translations are set.

  The german and english titles must be set, other wise they will be filled with the key.
  Same goes for descriptions as soon as at least one description is set.
*/
export function validateTranslation({
  translation,
  key,
}: ValidateTranslationProps): UISchemaElementTranslations {
  const validatedTranslation: UISchemaElementTranslations = {
    title: {
      de: translation?.title?.de || key,
      en: translation?.title?.en || key,
    },
  };
  if (translation?.description) {
    validatedTranslation.description = {
      de: translation?.description?.de || key,
      en: translation?.description?.en || key,
    };
  }
  if (translation?.enumNames) {
    validatedTranslation.enumNames = {
      ...translation?.enumNames,
    };
  }
  if (translation?.tabNames) {
    validatedTranslation.tabNames = {
      ...translation?.tabNames,
    };
  }
  if (translation?.entryTitle) {
    validatedTranslation.entryTitle = {
      ...translation?.entryTitle,
    };
  }
  return validatedTranslation;
}

// checks if there any rendered children. Rows, Fieldsets, etc. are skipped if the do not render
// any children in the current context
export function hasRenderedChildren(children: any[]) {
  if (!children?.length) return false;

  const s = children.some((child) => {
    if (
      !React.isValidElement<{
        data?: { isInput?: boolean };
        children?: React.ReactNode;
      }>(child)
    )
      return false;
    if (child.props.data?.isInput) return true;
    const nestedChildren = React.Children.toArray(child.props.children);
    if (nestedChildren.length > 0) return hasRenderedChildren(nestedChildren);
    return false;
  });
  if (s) return true;

  return false;
}

// similar to hasRenderedChildren but checks the mapping before it was mapped to React elements
export function hasMappedChildren(children: any, currentScope: UISchemaScope) {
  const mappedChildren = flattenUISchema(children);
  if (!mappedChildren?.length) return false;

  const s = mappedChildren.some((child) => {
    if (/.+Field/.test(child.component) && child.scopes?.includes(currentScope))
      return true;
    if (child.children && Object.keys(child.children).length) {
      return hasMappedChildren(child.children, currentScope);
    }
    return false;
  });
  if (s) return true;

  return false;
}
